import { type Driver, type DriveStep, driver } from "driver.js"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { UserContext } from "~/context/UserContext"
import { captureEvent } from "~/modules/analytics/capture"
import { useTranslate } from "~/ur-react-hooks"
import { trpc } from "~/utils/trpc"
import "driver.js/dist/driver.css"

const TOUR_COMPLETED_KEY = "arbeidsark:generator-tour-completed"
const TOUR_VERSION_KEY = "arbeidsark:generator-tour-version"
const CURRENT_TOUR_VERSION = 1
const TOUR_SOURCE = "generator_tour"
const TOUR_STEP_IDS = [
  "welcome",
  "preview",
  "tabs",
  "settings",
  "pages",
  "page_configurations",
  "configure",
  "module_regenerate",
  "global_regenerate",
  "download",
  "save",
  "done",
] as const

interface UseGeneratorTourOptions {
  onPageChange?: (pages: number) => void
}

export function useGeneratorTour(_options?: UseGeneratorTourOptions) {
  const [isActive, setIsActive] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const driverRef = useRef<Driver | null>(null)
  const pageChangeListenerRef = useRef<(() => void) | null>(null)
  const activeStepIndexRef = useRef(0)
  const user = useContext(UserContext)
  const trpcContext = trpc.useUtils()
  const { mutate: completeWebsiteTutorial } =
    trpc.users.completeWebsiteTutorial.useMutation({
      onSuccess: () => {
        void trpcContext.users.me.invalidate()
      },
    })

  const translations = useTranslate({
    welcome: {
      title: "onboarding.tour.welcome.title",
      description: "onboarding.tour.welcome.description",
    },
    preview: {
      title: "onboarding.tour.preview.title",
      description: "onboarding.tour.preview.description",
    },
    tabs: {
      title: "onboarding.tour.tabs.title",
      description: "onboarding.tour.tabs.description",
    },
    settings: {
      title: "onboarding.tour.settings.title",
      description: "onboarding.tour.settings.description",
    },
    pages: {
      title: "onboarding.tour.pages.title",
      description: "onboarding.tour.pages.description",
    },
    pageConfig: {
      title: "onboarding.tour.page-config.title",
      description: "onboarding.tour.page-config.description",
    },
    configure: {
      title: "onboarding.tour.configure.title",
      description: "onboarding.tour.configure.description",
    },
    moduleRegenerate: {
      title: "onboarding.tour.module-regenerate.title",
      description: "onboarding.tour.module-regenerate.description",
    },
    globalRegenerate: {
      title: "onboarding.tour.global-regenerate.title",
      description: "onboarding.tour.global-regenerate.description",
    },
    download: {
      title: "onboarding.tour.download.title",
      description: "onboarding.tour.download.description",
    },
    save: {
      title: "onboarding.tour.save.title",
      description: "onboarding.tour.save.description",
    },
    done: {
      title: "onboarding.tour.done.title",
      description: "onboarding.tour.done.description",
    },
    buttons: {
      next: "onboarding.tour.buttons.next",
      prev: "onboarding.tour.buttons.prev",
      done: "onboarding.tour.buttons.done",
      skip: "onboarding.tour.buttons.skip",
    },
  })

  const markTourCompleted = useCallback(() => {
    const completed = localStorage.getItem(TOUR_COMPLETED_KEY)
    const version = localStorage.getItem(TOUR_VERSION_KEY)
    const alreadyCompletedForVersion =
      completed === "true" && version === String(CURRENT_TOUR_VERSION)

    if (!alreadyCompletedForVersion && !user?.hasCompletedTutorial) {
      captureEvent("complete_intro_tutorial", {
        source: TOUR_SOURCE,
        tour_version: CURRENT_TOUR_VERSION,
      })
    }

    localStorage.setItem(TOUR_COMPLETED_KEY, "true")
    localStorage.setItem(TOUR_VERSION_KEY, String(CURRENT_TOUR_VERSION))

    if (user && !user.hasCompletedTutorial) {
      completeWebsiteTutorial()
    }
  }, [completeWebsiteTutorial, user])

  const isTourCompleted = useCallback(() => {
    if (user?.hasCompletedTutorial) {
      return true
    }

    const completed = localStorage.getItem(TOUR_COMPLETED_KEY)
    const version = localStorage.getItem(TOUR_VERSION_KEY)
    return completed === "true" && version === String(CURRENT_TOUR_VERSION)
  }, [user])

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_COMPLETED_KEY)
    localStorage.removeItem(TOUR_VERSION_KEY)
  }, [])

  const createSteps = useCallback((): DriveStep[] => {
    return [
      // Step 1: Welcome popup (no element - centered modal)
      {
        popover: {
          title: translations.welcome.title,
          description: translations.welcome.description,
          side: "over" as const,
          align: "center" as const,
        },
      },
      // Step 2: Preview area
      {
        element: '[data-tour="preview-area"]',
        popover: {
          title: translations.preview.title,
          description: translations.preview.description,
          side: "left" as const,
          align: "center" as const,
        },
      },
      // Step 3: Subject tabs
      {
        element: '[data-tour="subject-tabs"]',
        popover: {
          title: translations.tabs.title,
          description: translations.tabs.description,
          side: "bottom" as const,
          align: "center" as const,
        },
      },
      // Step 4: General settings
      {
        element: '[data-tour="general-settings"]',
        popover: {
          title: translations.settings.title,
          description: translations.settings.description,
          side: "right" as const,
          align: "start" as const,
        },
      },
      // Step 5: Pages input (interactive - wait for + click)
      {
        element: '[data-tour="pages-input"]',
        popover: {
          title: translations.pages.title,
          description: translations.pages.description,
          side: "right" as const,
          align: "center" as const,
        },
      },
      // Step 6: Page configurations
      {
        element: '[data-tour="page-configurations"]',
        popover: {
          title: translations.pageConfig.title,
          description: translations.pageConfig.description,
          side: "right" as const,
          align: "start" as const,
        },
      },
      // Step 7: Configure button
      {
        element: '[data-tour="module-configure-button"]',
        popover: {
          title: translations.configure.title,
          description: translations.configure.description,
          side: "right" as const,
          align: "center" as const,
        },
      },
      // Step 8: Module regenerate button
      {
        element: '[data-tour="module-regenerate-button"]',
        popover: {
          title: translations.moduleRegenerate.title,
          description: translations.moduleRegenerate.description,
          side: "right" as const,
          align: "center" as const,
        },
      },
      // Step 9: Global regenerate button
      {
        element: '[data-tour="global-regenerate-button"]',
        popover: {
          title: translations.globalRegenerate.title,
          description: translations.globalRegenerate.description,
          side: "right" as const,
          align: "center" as const,
        },
      },
      // Step 10: Download button
      {
        element: '[data-tour="download-button"]',
        popover: {
          title: translations.download.title,
          description: translations.download.description,
          side: "bottom" as const,
          align: "center" as const,
        },
      },
      // Step 11: Save button
      {
        element: '[data-tour="save-button"]',
        popover: {
          title: translations.save.title,
          description: translations.save.description,
          side: "bottom" as const,
          align: "center" as const,
        },
      },
      // Step 12: Done (no element - centered modal)
      {
        popover: {
          title: translations.done.title,
          description: translations.done.description,
          side: "over" as const,
          align: "center" as const,
        },
      },
    ]
  }, [translations])

  const startTour = useCallback(() => {
    // Clean up any existing driver instance
    if (driverRef.current) {
      driverRef.current.destroy()
    }

    const steps = createSteps()
    const getStepTrackingProperties = (step: DriveStep | undefined) => {
      const stepIndex = step ? steps.indexOf(step) : -1
      const fallbackStepIndex =
        stepIndex >= 0 ? stepIndex : activeStepIndexRef.current
      const safeStepIndex = Math.min(
        Math.max(fallbackStepIndex, 0),
        TOUR_STEP_IDS.length - 1,
      )

      return {
        source: TOUR_SOURCE,
        tour_version: CURRENT_TOUR_VERSION,
        step_index: safeStepIndex + 1,
        step_id: TOUR_STEP_IDS[safeStepIndex] ?? "unknown",
        total_steps: TOUR_STEP_IDS.length,
        is_last_step: safeStepIndex === TOUR_STEP_IDS.length - 1,
      }
    }

    const driverInstance = driver({
      showProgress: true,
      steps,
      nextBtnText: translations.buttons.next,
      prevBtnText: translations.buttons.prev,
      doneBtnText: translations.buttons.done,
      popoverClass: "arbeidsark-tour-popover",
      stagePadding: 0,
      stageRadius: 0,
      overlayColor: "#000000",
      overlayOpacity: 0.7,
      disableActiveInteraction: true,
      smoothScroll: true,
      onHighlightStarted: (element, step) => {
        const stepIndex = steps.indexOf(step)
        setCurrentStep(stepIndex)
        if (stepIndex >= 0) {
          activeStepIndexRef.current = stepIndex
        }

        // Manually scroll sidebar elements into view since the sidebar has its own scroll context
        if (element) {
          const sidebarContent = element.closest(
            "[data-tour-scroll-container]",
          ) as HTMLElement | null
          if (sidebarContent) {
            // Check if element is already visible in the scroll container
            const elementRect = element.getBoundingClientRect()
            const containerRect = sidebarContent.getBoundingClientRect()

            const isVisible =
              elementRect.top >= containerRect.top &&
              elementRect.bottom <= containerRect.bottom

            // Only scroll if the element is not already visible
            if (!isVisible) {
              setTimeout(() => {
                element.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                  inline: "nearest",
                })
              }, 50)
            }
          }
        }
      },
      onDeselected: () => {
        // Clean up any listeners
        if (pageChangeListenerRef.current) {
          pageChangeListenerRef.current()
          pageChangeListenerRef.current = null
        }
      },
      onNextClick: (_element, step, opts) => {
        captureEvent(
          "complete_intro_tutorial_step",
          getStepTrackingProperties(step),
        )

        if (opts.driver.isLastStep()) {
          markTourCompleted()
          opts.driver.destroy()
          return
        }

        opts.driver.moveNext()
      },
      onPrevClick: (_element, _step, opts) => {
        opts.driver.movePrevious()
      },
      onCloseClick: (_element, step, opts) => {
        captureEvent("skip_intro_tutorial", {
          ...getStepTrackingProperties(step),
          reason: "close_button",
        })
        opts.driver.destroy()
      },
      onDestroyed: () => {
        setIsActive(false)
      },
    })

    driverRef.current = driverInstance
    setIsActive(true)
    driverInstance.drive()
  }, [createSteps, translations.buttons, markTourCompleted])

  const stopTour = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy()
      driverRef.current = null
    }
    setIsActive(false)
  }, [])

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy()
      }
      if (pageChangeListenerRef.current) {
        pageChangeListenerRef.current()
      }
    }
  }, [])

  return {
    startTour,
    stopTour,
    resetTour,
    isActive,
    currentStep,
    isTourCompleted,
    markTourCompleted,
  }
}

export default useGeneratorTour
